import { FC } from 'react';
import { useFormikContext } from 'formik';
import { ModeCalculTarification, TarifDto } from 'api/funnel';
import { FormikDropdown } from 'components/forms/formik/FormikDropdown';
import { FormikInputText } from 'components/forms/formik/FormikInputText';
import { FormField } from 'components/forms/FormField';
import {
  MODE_CALCUL_OPTIONS,
  isExpressionMode,
  isMontantHTMode,
  isTauxMode,
} from '../funnelEnums';
import { buildTarifAmounts } from '../funnelFormat';
import { ExpressionBuilder, ExpressionVariable } from './expression/ExpressionBuilder';

/**
 * Bloc « mode de calcul + valeur » d'une tarification, préfixé pour cohabiter plusieurs
 * fois dans un même formulaire (tarif d'un frais, sa taxe, prime pure d'une garantie…).
 *
 * L'ancien back-office dupliquait ce bloc à huit endroits, chacun ayant dérivé :
 * certains affichaient le champ « taux » même en mode montant fixe, et le payload
 * partait alors avec deux montants concurrents que le back rejetait.
 */

export interface TarifFormValues {
  modeCalcul: ModeCalculTarification;
  /** Montant OU taux selon le mode ; un seul champ de saisie pour l'utilisateur. */
  montant: number | '';
  expression: string;
}

export const emptyTarifValues = (
  modeCalcul: ModeCalculTarification = ModeCalculTarification.MontantFixe
): TarifFormValues => ({
  modeCalcul,
  montant: '',
  expression: '',
});

/** Reconstruit les valeurs de formulaire à partir d'une tarification lue. */
export const tarifToFormValues = (tarif?: TarifDto | null): TarifFormValues => {
  if (!tarif) return emptyTarifValues();

  const mode = tarif.modeCalcul ?? ModeCalculTarification.MontantFixe;
  const montant = isTauxMode(mode)
    ? tarif.taux
    : isMontantHTMode(mode)
      ? tarif.montantFixeHT
      : tarif.montantFixe;

  return {
    modeCalcul: mode,
    montant: montant ?? '',
    expression: tarif.expression ?? '',
  };
};

/** Convertit les valeurs de formulaire en commande de tarification. */
export const tarifFromFormValues = (values: TarifFormValues) => ({
  modeCalcul: values.modeCalcul,
  ...buildTarifAmounts(
    values.modeCalcul,
    values.montant === '' ? null : Number(values.montant),
    values.expression.trim() || null
  ),
});

/** Préfixe les clés d'un TarifFormValues pour les aplatir dans un formulaire parent. */
export const prefixTarifValues = (prefix: string, values: TarifFormValues) => ({
  [`${prefix}ModeCalcul`]: values.modeCalcul,
  [`${prefix}Montant`]: values.montant,
  [`${prefix}Expression`]: values.expression,
});

/** Opération inverse de prefixTarifValues. */
export const extractTarifValues = (
  prefix: string,
  values: Record<string, unknown>
): TarifFormValues => ({
  modeCalcul: values[`${prefix}ModeCalcul`] as ModeCalculTarification,
  montant: values[`${prefix}Montant`] as number | '',
  expression: (values[`${prefix}Expression`] as string) ?? '',
});

interface TarifFieldsProps {
  /** Préfixe des noms de champs, ex. « tarif » → tarifModeCalcul, tarifMontant… */
  prefix: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  /** Variables proposées par le constructeur de formule. */
  variables?: ExpressionVariable[];
  /** Masque le mode Expression là où le back ne l'accepte pas. */
  allowExpression?: boolean;
  /** Masque le mode « Montant fixe TTC » là où le moteur de tarification ne le calcule pas. */
  allowMontantTTC?: boolean;
  /**
   * Le taux est un multiplicateur, pas un pourcentage.
   *
   * C'est le cas de la taxe : le serveur calcule `prixHT × (taux − 1)`, donc 1,09 vaut
   * 9 %. Saisir « 9 » y appliquerait 800 % sans le moindre message d'erreur — le champ
   * l'annonce donc explicitement et rappelle la conversion.
   */
  rateAsMultiplier?: boolean;
  /** Aide affichée sous le libellé (ex. rémunération exprimée en pourcentage). */
  hint?: string;
}

export const TarifFields: FC<TarifFieldsProps> = ({
  prefix,
  label,
  required,
  disabled,
  variables = [],
  allowExpression = true,
  allowMontantTTC = true,
  rateAsMultiplier = false,
  hint,
}) => {
  const { values, setFieldValue, getFieldMeta } = useFormikContext<Record<string, unknown>>();
  const mode = values[`${prefix}ModeCalcul`] as ModeCalculTarification | undefined;
  const expression = (values[`${prefix}Expression`] as string) ?? '';

  const modeItems = MODE_CALCUL_OPTIONS.filter(
    (option) =>
      (allowExpression || option.value !== ModeCalculTarification.Expression) &&
      (allowMontantTTC || option.value !== ModeCalculTarification.MontantFixe)
  ).map((option) => ({ key: option.value, label: option.label }));

  return (
    <div className="flex flex-col gap-4">
      <FormikDropdown
        name={`${prefix}ModeCalcul`}
        label={label}
        labelTooltip={hint}
        required={required}
        disabled={disabled}
        items={modeItems}
      />

      {isExpressionMode(mode) ? (
        <FormField label="Expression" required={required} name={`${prefix}Expression`}>
          <ExpressionBuilder
            value={expression}
            initialValue={(getFieldMeta(`${prefix}Expression`).initialValue as string) ?? ''}
            onChange={(next) => setFieldValue(`${prefix}Expression`, next)}
            variables={variables}
            disabled={disabled}
          />
        </FormField>
      ) : (
        <div className="flex flex-col gap-1">
          <FormikInputText
            name={`${prefix}Montant`}
            label={
              isTauxMode(mode)
                ? rateAsMultiplier
                  ? 'Taux (multiplicateur)'
                  : 'Taux (%)'
                : isMontantHTMode(mode)
                  ? 'Montant HT (€)'
                  : 'Montant TTC (€)'
            }
            required={required}
            disabled={disabled}
            type="number"
            step="any"
            placeholder={isTauxMode(mode) && rateAsMultiplier ? '1,09' : '0'}
          />
          {isTauxMode(mode) && rateAsMultiplier && (
            <p className="text-xs text-amber-700">
              Multiplicateur appliqué au prix HT, et non un pourcentage : saisir
              <strong> 1,09</strong> pour 9 %.
            </p>
          )}
        </div>
      )}
    </div>
  );
};
